import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BarChart3, Inbox, PenLine, Send, Megaphone, Users, Mail, Newspaper,
  Settings, UserCog, LogOut, Menu, X, Moon, Sun, Rss, Images, BookOpen, Compass,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { tourPending } from "@/components/TourGuide";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Panel", icon: BarChart3, id: "panel" },
  { to: "/ingesta", label: "Ingesta", icon: Inbox, id: "ingesta" },
  { to: "/fuentes", label: "Fuentes", icon: Rss, id: "fuentes" },
  { to: "/redaccion", label: "Redacción", icon: PenLine, id: "redaccion" },
  { to: "/biblioteca", label: "Biblioteca", icon: Images, id: "biblioteca" },
  { to: "/difusion", label: "Difusión", icon: Send, id: "difusion" },
  { to: "/banners", label: "Anuncios", icon: Megaphone, id: "banners" },
  { to: "/boletines", label: "Boletines", icon: Mail, id: "boletines" },
  { to: "/suscriptores", label: "Suscriptores", icon: Users, id: "suscriptores" },
  { to: "/medios", label: "Medios", icon: Newspaper, id: "medios" },
  { to: "/equipo", label: "Equipo", icon: UserCog, id: "equipo" },
  { to: "/documentacion", label: "Documentación", icon: BookOpen, id: "documentacion" },
  { to: "/ajustes", label: "Ajustes", icon: Settings, id: "ajustes" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    if (user?.role === "redactor" && tourPending()) {
      window.dispatchEvent(new Event("redactia-tour"));
    }
  }, [user?.role]);

  const toggleTheme = () => {
    document.documentElement.classList.toggle("dark");
    setDark(document.documentElement.classList.contains("dark"));
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row relative z-10">
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-[248px] shrink-0 border-r border-border bg-card
          flex flex-col
          transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-border">
          <Logo />
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground leading-relaxed mt-3">
            Agencia Multimedios Sociedad de la Información, SRL
          </div>
        </div>
        <nav className="p-3 space-y-0.5 overflow-y-auto flex-1 min-h-0">
          {NAV.map(({ to, label, icon: Icon, id }) => {
            const active = to === "/" ? pathname === "/" : pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                data-testid={`nav-${id}`}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors
                  ${active
                    ? "bg-accent text-accent-foreground font-medium"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-border p-4 shrink-0">
          <div className="text-sm font-medium truncate" data-testid="current-user">{user?.name}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-3">{user?.role}</div>
          <Button variant="outline" size="sm" className="w-full mb-2" onClick={() => window.dispatchEvent(new Event("redactia-tour"))}
                  data-testid="start-tour-btn">
            <Compass className="h-3.5 w-3.5 mr-1.5" /> Recorrido guiado
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn" className="flex-1">
              <LogOut className="h-3.5 w-3.5 mr-1.5" /> Salir
            </Button>
            <Button variant="outline" size="sm" onClick={toggleTheme} data-testid="theme-toggle">
              {dark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </aside>

      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card px-4 py-3">
        <span className="font-bold"><Logo compact /></span>
        <Button variant="ghost" size="icon" onClick={() => setOpen(!open)} data-testid="menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </header>

      <main className="flex-1 min-w-0 overflow-x-hidden p-4 sm:p-8 lg:p-12 max-w-[1500px]">{children}</main>
    </div>
  );
}

export function PageHeader({ kicker, title, description, children }) {
  return (
    <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4 rise">
      <div>
        <div className="kicker text-primary">{kicker}</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{title}</h1>
        {description && (
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{description}</p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}
